/**
 * Защищённый доступ к API браузера.
 *
 * Любое обращение к отпечатковому API может бросить исключение: движок его не
 * поддерживает, антидетект пропатчил геттер и тот падает, расширение отозвало
 * доступ. Падение одного сигнала не должно ронять сбор целиком — поэтому
 * абсолютно всё в коллекторе идёт через эти обёртки.
 */

/**
 * Выполняет функцию, возвращает `fallback` при любом исключении.
 * `undefined` тоже приводится к `fallback` — отсутствующее и недоступное
 * свойство для нас одно и то же, и в типе это отражено через Exclude.
 */
export function attempt<T>(fn: () => T): Exclude<T, undefined> | null;
export function attempt<T, F>(fn: () => T, fallback: F): T | F;
export function attempt<T>(fn: () => T, fallback: unknown = null): unknown {
  try {
    const value = fn();
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

/** Асинхронный вариант `attempt`. */
export async function attemptAsync<T>(fn: () => Promise<T> | T): Promise<Exclude<T, undefined> | null>;
export async function attemptAsync<T, F>(fn: () => Promise<T> | T, fallback: F): Promise<T | F>;
export async function attemptAsync<T>(
  fn: () => Promise<T> | T,
  fallback: unknown = null,
): Promise<unknown> {
  try {
    const value = await fn();
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

export class TimeoutError extends Error {
  constructor(public readonly label: string, public readonly ms: number) {
    super(`${label}: превышен таймаут ${ms} мс`);
    this.name = 'TimeoutError';
  }
}

/** Ограничивает время ожидания промиса. Зависший сигнал не блокирует отчёт. */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Промис, который разрешается через `ms` миллисекунд. */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Отдаёт управление браузеру, чтобы длинный сбор не морозил вкладку.
 * `scheduler.yield()` там, где он есть; иначе — макротаск.
 */
export function yieldToBrowser(): Promise<void> {
  const scheduler = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  if (scheduler?.yield) {
    return attempt(() => scheduler.yield!(), delay(0)) as Promise<void>;
  }
  return delay(0);
}

/** Монотонное время в миллисекундах. */
export function now(): number {
  return attempt(() => performance.now(), Date.now());
}

/** Свойство объекта без вызова геттеров-ловушек напрямую на объекте. */
export function describe(obj: object, key: string): PropertyDescriptor | null {
  return attempt(() => Object.getOwnPropertyDescriptor(obj, key));
}

/** Дескриптор свойства на прототипе, а не на самом объекте. */
export function describeOnPrototype(obj: object, key: string): PropertyDescriptor | null {
  return attempt(() => {
    let proto: object | null = Object.getPrototypeOf(obj);
    while (proto) {
      const d = Object.getOwnPropertyDescriptor(proto, key);
      if (d) return d;
      proto = Object.getPrototypeOf(proto);
    }
    return null;
  });
}

/** Все перечислимые ключи объекта и его прототипов, в порядке обхода. */
export function chainKeys(obj: object, maxDepth = 3): string[] {
  return (
    attempt(() => {
      const keys: string[] = [];
      let proto: object | null = obj;
      let depth = 0;
      while (proto && depth < maxDepth) {
        for (const k of Object.getOwnPropertyNames(proto)) {
          if (!keys.includes(k)) keys.push(k);
        }
        proto = Object.getPrototypeOf(proto);
        depth++;
      }
      return keys;
    }, []) ?? []
  );
}

/** Значение по пути вида `navigator.userAgentData.platform`. */
export function pick(root: unknown, path: string): unknown {
  return attempt(() => {
    let current: unknown = root;
    for (const part of path.split('.')) {
      if (current === null || current === undefined) return null;
      current = (current as Record<string, unknown>)[part];
    }
    return current;
  });
}

/** Округление до заданного числа знаков — гасит незначимый шум в числах. */
export function round(value: number, digits = 4): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

/** Приводит неизвестную ошибку к строке для поля `error` в метаданных. */
export function errorMessage(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}
